import React from 'react';
import PropTypes from 'prop-types';

import GraphTitle from 'app/shared/components/Graphs/GraphTitle';
import GraphEmptyMessage from 'app/shared/components/Graphs/GraphEmptyMessage';
import GraphTotalTooltip from 'app/shared/components/Graphs/GraphTotalTooltip';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';

import GraphConstants from 'app/shared/constants/GraphConstants';

import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Legend,
    Tooltip,
} from 'recharts';

const propTypes = {
    brand: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    isLoading: PropTypes.bool,
    showTabs: PropTypes.bool,
};

const defaultProps = {
    isLoading: false,
    showTabs: false,
};

const GRAPH = GraphConstants.GRAPHS.LEAD_CLICK;
const TABS = GraphConstants.GRAPH_TABS;
const barOneKey = GraphConstants.GRAPH_DATA_TYPES.LEADS;
const barTwoKey = GraphConstants.GRAPH_DATA_TYPES.CLICKS;

const LeadClickGraph = ({
    showTabs,
    brand,
    isLoading,
    data,
}) => {
    const [activeTab, setActiveTab] = React.useState(showTabs ? TABS.AVERAGE : TABS.DEFAULT);

    React.useEffect(() => {
        if (!showTabs) {
            setActiveTab(TABS.DEFAULT);
        }
    }, [showTabs]);

    const handleTabClick = index => setActiveTab(index === 0 ? TABS.AVERAGE : TABS.TOTAL);

    const configs = GRAPH[activeTab];
    const currentData = data[configs.DATA_TYPE];
    const leadCount = parseInt(currentData[barOneKey] || 0, 10);
    const clickCount = parseInt(currentData[barTwoKey] || 0, 10);
    const number = isLoading ? 0 : leadCount + clickCount;
    const title = `Contacts ${clickCount ? 'and clicks' : ''}: ${number || '-'}`;

    const renderGraph = () => (
        <div className="Graph-container">
            {currentData.isEmpty && <GraphEmptyMessage message="No contacts or clicks available" />}
            <ResponsiveContainer width="100%" height={300}>
                <BarChart data={currentData.months} key={barOneKey}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <Tooltip content={<GraphTotalTooltip />} />
                    <Legend align="left" verticalAlign="bottom" wrapperStyle={{ marginLeft: 60 }} />
                    <Bar
                        dataKey={barOneKey}
                        maxBarSize={20}
                        fill={GraphConstants.COLORS[brand].primary}
                    />
                    {clickCount && (
                        <Bar
                            dataKey={barTwoKey}
                            maxBarSize={20}
                            fill={GraphConstants.COLORS[brand].secondary}
                        />
                    )}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );

    return (
        <div className="Graph">
            <GraphTitle
                title={title}
                isLoading={isLoading}
                showTabs={showTabs}
                onTabClick={handleTabClick}
                activeIndex={configs.TAB_INDEX}
            />
            {isLoading ? <SpinnerContainer customClass="Graph-Spinner" /> : renderGraph(currentData)}
        </div>
    );
};

LeadClickGraph.propTypes = propTypes;
LeadClickGraph.defaultProps = defaultProps;

export default LeadClickGraph;
